import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { PageContent, PagePanel, PanelBody, PanelContent, PaneledPage, PanelHeader, PanelToolbar } from '@root/Design/Layout';
import {
    ActionIcon,
    Anchor,
    Autocomplete,
    Box,
    Button,
    Card,
    CopyButton,
    createStyles,
    Divider,
    FileInput,
    Grid,
    Group,
    List,
    Loader,
    Modal,
    Paper,
    Radio,
    Select,
    Space,
    Stack,
    Text,
    TextInput,
    Title,
    useMantineTheme,
} from '@mantine/core';
import { useNav } from '@root/Services/NavigationService';
import { useEffect, useState } from 'react';
import { getCompanyGetSingleSignOn, postCompanySetupSingleSignOn, deleteCompanyRemoveSingleSignOn } from '@apis/Customers';
import { SingleSignOnClaimRole, SingleSignOnConfig } from '@apis/Customers/model';
import { useForm } from '@mantine/form';
import { colorPalette, CustomColors, theme } from '@root/Design/Themes';
import { Copy, Download, InfoCircle, Upload, FileCertificate, Trash, Plus } from 'tabler-icons-react';
import React from 'react';
import { ConfigService } from '@root/Services/ConfigService';
import { useDi } from '@root/Services/DI';
import { CompanyRoleService } from '../Users/CompanyRoleService';
import { useAuthZValues } from '@root/Services/AuthorizationService';
import { NotificationService } from '@root/Services/Notification/NotificationService';

export function ConfigureSSO() {
    const identityProvider = ['Azure', 'Okta', 'One Login', 'Ping Identity'];
    const signRequestAlgorithm = ['rsa-sha256', 'rsa-sha1'];
    const signReqAlgDigest = ['sha256', 'sha1'];
    const protocolBinding = ['HTTP-POST', 'HTTP-Redirect'];
    const auth0Domain = 'cloudsaver.auth0.com';
    const cerUrl = `https://${auth0Domain}/cer`;
    const pemUrl = `https://${auth0Domain}/pem`;
    const rawPemUrl = `https://${auth0Domain}/rawpem`;
    const pb7Url = `https://${auth0Domain}/pb7`;
    const fingerprintUrl = `https://${auth0Domain}/fingerprint`;
    const configSvc = useDi(ConfigService);
    const notificationSvc = useDi(NotificationService);

    const [configured, setConfigured] = useState<boolean>(false);
    const [connectionName, setConnectionName] = useState<string>('');
    const [metadataUrl, setMetadataUrl] = useState<string>('#');
    const [entityId, setEntityId] = useState<string>('');
    const [signOnUrl, setSignOnUrl] = useState<string>('');
    const [signOutUrl, setSignOutUrl] = useState<string>('');
    const [certSubject, setCertSubject] = useState<string>('');
    const [isValid, setIsValid] = useState(false);
    const [showConfirmRemove, setShowConfirmRemove] = useState<boolean>(false);
    const [disableConfirmRemove, setDisableConfirmRemove] = useState<boolean>(true);
    const companyRoleSvc = useDi(CompanyRoleService);
    const { classes } = useStyles();
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [isDeletingCert, setIsDeletingCert] = useState(false);
    const [roles, setRoles] = useState([] as { value: number; label: string }[]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const userPermissions = useAuthZValues({
        canManage: { SSO: 'Manage' },
    });

    const theme = useMantineTheme();
    const { ascend } = useNav();

    const validateURL = (value: string) => {
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    };

    const form = useForm({
        initialValues: {
            IdentityProvider: '',
            DomainName: '',
            SignInUrl: '',
            SignOutUrl: '',
            SignRequestAlgorithm: signRequestAlgorithm[0],
            SignRequestAlgorithmDigest: signReqAlgDigest[0],
            ProtocolBinding: protocolBinding[0],
            X509SigningCertificate: null,
            DefaultUserRoleId: null as number | null,
            GroupClaimName: '',
            ClaimRoles: [] as SingleSignOnClaimRole[],
        },
        validate: (vals) => {
            const errors: Record<string, string> = {};

            if (!vals.IdentityProvider) errors.IdentityProvider = 'Identity provider required';
            if (vals.SignInUrl && !validateURL(vals.SignInUrl)) errors.SignInUrl = 'Invalid URL';
            if (vals.SignOutUrl && !validateURL(vals.SignOutUrl)) errors.SignOutUrl = 'Invalid URL';
            if (!vals.DefaultUserRoleId) errors.DefaultUserRoleId = 'Default user role required';

            (vals.ClaimRoles ?? []).forEach((r, idx) => {
                const hasName = !!r.ExternalClaimValue?.trim();
                const hasRole = r.RoleId != null && r.RoleId > 0;
                if (hasName !== hasRole) {
                    if (!hasName) errors[`ClaimRoles.${idx}.ExternalClaimValue`] = 'Enter a group or clear the row';
                    if (!hasRole) errors[`ClaimRoles.${idx}.RoleId`] = 'Select a role or clear the row';
                }
            });

            return errors;
        },
        validateInputOnChange: true,
    });

    useEffect(() => {
        const { hasErrors } = form.validate();
        setIsValid(!hasErrors);
    }, [form.values]);

    const createRadioButtons = (items: string[]) =>
        items.map((item, index) => <Radio key={index} label={item.startsWith('HTTP') ? item : item.toUpperCase()} value={item} />);

    const submitSSOConfig = async (config: SingleSignOnConfig) => {
        return await postCompanySetupSingleSignOn(config);
    };

    const buildPayload = (values: typeof form.values) => {
        return {
            ...values,
            ClaimRoles: (values.ClaimRoles ?? [])
                .filter((r) => (r.ExternalClaimValue ?? '').trim() && (r.RoleId ?? 0) > 0)
                .map((r) => ({
                    ...r,
                    ExternalClaimValue: r.ExternalClaimValue!.trim(),
                })),
        } as SingleSignOnConfig;
    };

    const readFileAsBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onerror = () => reject(new Error('Failed to read certificate file.'));
            fr.onload = () => {
                const base64 = (fr.result as string)?.split(',')[1] ?? '';
                resolve(base64);
            };
            fr.readAsDataURL(file);
        });
    };

    const handleSubmit = async () => {
        const { hasErrors } = form.validate();
        if (hasErrors) {
            return;
        }

        await saveChanges(form.values);
    };

    const saveChanges = async (values: typeof form.values) => {
        try {
            setIsLoading(true);
            const ssoConfig = buildPayload(values);
            if (values.X509SigningCertificate) {
                const base64 = await readFileAsBase64(values.X509SigningCertificate);
                ssoConfig.Base64X509SigningCertificate = base64;
            }
            await submitSSOConfig(ssoConfig);
            ascend();
        } catch (err) {
            notificationSvc.notify('SSO Setup Failed', '', 'error', null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmDeleteChange = (e: React.ChangeEvent<HTMLInputElement>) => setDisableConfirmRemove(e.target.value !== 'DELETE');

    const handleRemoveSSO = async () => {
        try {
            setIsLoading(true);
            await deleteCompanyRemoveSingleSignOn();
            ascend();
        } catch (error) {
            notificationSvc.notify('SSO Deletion Failed', '', 'error', null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        window.history.back();
    };

    const handleBlur = (field: string) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const userRoles = await companyRoleSvc.getRoleList();
            userRoles.sort((a, b) => a.Role?.Name?.localeCompare(b.Role?.Name ?? '') || a.Role?.Id! - b.Role?.Id!);
            const roleOptions = userRoles.map((role) => ({
                value: role.Role!.Id!,
                label: role.Role!.Name!,
            }));
            setRoles(roleOptions);

            let config = await getCompanyGetSingleSignOn();
            if (config) {
                if (config.SignInUrl) {
                    setConfigured(true);
                }
                if (config.ConnectionName) {
                    setConnectionName(config.ConnectionName);
                    setMetadataUrl(`https://${auth0Domain}/samlp/metadata?connection=${config.ConnectionName}`);
                    setEntityId(`urn:auth0:cloudsaver:${config.ConnectionName}`);
                    setSignOnUrl(`https://${auth0Domain}/login/callback?connection=${config.ConnectionName}`);
                    setSignOutUrl(`https://${auth0Domain}/logout`);
                    setCertSubject(config.CertificateSubject!);
                }

                form.setValues({
                    ...form.values,
                    ...config,
                    ClaimRoles: (config.ClaimRoles ?? []) as SingleSignOnClaimRole[],
                } as any);

                const readOnlyRole = roleOptions.find((r) => r.label === 'Read-only');
                if (readOnlyRole && !form.values.DefaultUserRoleId) {
                    form.setFieldValue('DefaultUserRoleId', readOnlyRole.value);
                }
            }

            setIsLoading(false);
        })();
    }, []);

    const needsCertificate = (!configured && !form.values.X509SigningCertificate) || (isDeletingCert && !form.values.X509SigningCertificate);
    const isDisabled = !isValid || needsCertificate || !userPermissions.canManage;

    return (
        <PageContent>
            <PanelContent>
                <Group sx={{ flex: 1, overflow: 'hidden' }} spacing={0} noWrap align="stretch">
                    <Stack sx={{ flex: 1, height: '100%', overflow: 'hidden' }} spacing={0}>
                        <Box p={20} sx={{ flex: 1, overflow: 'auto' }}>
                            <Title order={4}>Configuration Details</Title>
                            <Space h="lg" />
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    <Grid gutter="xl" align="start">
                                        <Grid.Col xs={12} md={7}>
                                            <Autocomplete
                                                label="Identity Provider"
                                                data={identityProvider}
                                                limit={5}
                                                {...form.getInputProps('IdentityProvider')}
                                                onBlur={() => handleBlur('IdentityProvider')}
                                                error={touched.IdentityProvider ? form.errors.IdentityProvider : null}
                                            />
                                            <Space h="lg" />

                                            <TextInput label="Domain Name" {...form.getInputProps('DomainName')} />
                                            <Space h="lg" />

                                            <TextInput
                                                label="Sign-in URL"
                                                {...form.getInputProps('SignInUrl')}
                                                onBlur={() => handleBlur('SignInUrl')}
                                                error={touched.SignInUrl ? form.errors.SignInUrl : null}
                                            />
                                            <Space h="lg" />

                                            <TextInput
                                                label="Sign-out URL"
                                                {...form.getInputProps('SignOutUrl')}
                                                onBlur={() => handleBlur('SignOutUrl')}
                                                error={touched.SignOutUrl ? form.errors.SignOutUrl : null}
                                            />
                                            <Space h="lg" />

                                            {configured && !isDeletingCert ? (
                                                <>
                                                    <Text size="sm" weight={500} mb="xs">
                                                        X509 Signing Certificate
                                                    </Text>
                                                    <Paper
                                                        withBorder
                                                        radius="md"
                                                        p="md"
                                                        sx={(theme) => ({
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            backgroundColor: theme.white,
                                                        })}
                                                    >
                                                        <Group spacing="sm">
                                                            <FileCertificate size={24} />
                                                            <Text size="sm">Subject: {certSubject}</Text>
                                                        </Group>
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="error"
                                                            onClick={() => setIsDeletingCert(true)}
                                                            disabled={!userPermissions.canManage}
                                                        >
                                                            <Trash size={16} />
                                                        </ActionIcon>
                                                    </Paper>
                                                </>
                                            ) : (
                                                <FileInput
                                                    label={`${configured ? 'Update' : ''} X509 Signing Certificate`}
                                                    icon={<Upload />}
                                                    placeholder="Upload"
                                                    {...form.getInputProps('X509SigningCertificate')}
                                                />
                                            )}
                                            <Space h="lg" />

                                            <Select
                                                label="Default User Role"
                                                data={roles}
                                                {...form.getInputProps('DefaultUserRoleId')}
                                                onBlur={() => handleBlur('DefaultUserRoleId')}
                                                error={touched.DefaultUserRoleId ? form.errors.DefaultUserRoleId : null}
                                            />
                                        </Grid.Col>
                                        <Grid.Col xs={12} md={5}>
                                            <Radio.Group
                                                label="Sign Request Algorithm"
                                                orientation="vertical"
                                                {...form.getInputProps('SignRequestAlgorithm')}
                                            >
                                                {createRadioButtons(signRequestAlgorithm)}
                                            </Radio.Group>
                                            <Space h="lg" />
                                            <Radio.Group
                                                label="Sign Request Algorithm Digest"
                                                orientation="vertical"
                                                {...form.getInputProps('SignRequestAlgorithmDigest')}
                                            >
                                                {createRadioButtons(signReqAlgDigest)}
                                            </Radio.Group>
                                            <Space h="lg" />

                                            <Radio.Group label="Protocol Binding" orientation="vertical" {...form.getInputProps('ProtocolBinding')}>
                                                {createRadioButtons(protocolBinding)}
                                            </Radio.Group>
                                        </Grid.Col>
                                    </Grid>
                                    <Space h="xl" />
                                    <Paper withBorder radius="md" p="lg">
                                        <Title order={4}>Map token claims to Cloudsaver roles</Title>
                                        <Space h="sm" />
                                        <TextInput
                                            label="Group/roles claim name (in token)"
                                            description='Typically "groups" or URL to schema'
                                            placeholder="Group Name"
                                            {...form.getInputProps('GroupClaimName')}
                                        />

                                        <Space h="md" />

                                        <Stack spacing="xs">
                                            <Text weight={500}>Mappings</Text>

                                            {form.values.ClaimRoles?.length === 0 && (
                                                <Text size="sm" align="center">
                                                    No mappings yet, click below to add mapping
                                                </Text>
                                            )}

                                            {form.values.ClaimRoles?.map((row, idx) => (
                                                <Group key={idx} align="flex-start" noWrap>
                                                    <TextInput
                                                        label="SSO Group/Role"
                                                        placeholder="Group/Role Name"
                                                        style={{ flex: 1, minWidth: 0 }}
                                                        {...form.getInputProps(`ClaimRoles.${idx}.ExternalClaimValue`)}
                                                        error={form.errors[`ClaimRoles.${idx}.ExternalClaimValue`]}
                                                    />
                                                    <Select
                                                        label="CloudSaver Role"
                                                        placeholder="Select role"
                                                        data={roles.map((r) => ({ value: String(r.value), label: r.label }))}
                                                        searchable
                                                        clearable
                                                        nothingFound="No roles"
                                                        style={{ flex: 1, minWidth: 0 }}
                                                        value={
                                                            form.values.ClaimRoles[idx].RoleId != null
                                                                ? String(form.values.ClaimRoles[idx].RoleId)
                                                                : ''
                                                        }
                                                        onChange={(v) => form.setFieldValue(`ClaimRoles.${idx}.RoleId`, v ? Number(v) : undefined)}
                                                        error={form.errors[`ClaimRoles.${idx}.RoleId`]}
                                                    />
                                                    <ActionIcon
                                                        aria-label="Remove mapping"
                                                        color="error"
                                                        variant="light"
                                                        onClick={() => form.removeListItem('ClaimRoles', idx)}
                                                        mt={26}
                                                    >
                                                        <Trash size={18} />
                                                    </ActionIcon>
                                                </Group>
                                            ))}
                                            <Button
                                                leftIcon={<Plus size={16} />}
                                                variant="light"
                                                onClick={() =>
                                                    form.insertListItem('ClaimRoles', {
                                                        ExternalClaimValue: '',
                                                        RoleId: undefined,
                                                        Id: undefined,
                                                        SingleSignOnConfigId: undefined,
                                                    })
                                                }
                                                mt="xs"
                                            >
                                                Add mapping
                                            </Button>
                                        </Stack>
                                    </Paper>
                                    <Space h="lg" />
                                    {configured && (
                                        <>
                                            <Card radius="md" withBorder style={{ borderColor: colorPalette.errorSubText }}>
                                                <Group noWrap>
                                                    <InfoCircle style={{ alignSelf: 'flex-start' }} color={colorPalette.errorSubText} />
                                                    <Stack style={{ flexGrow: 1 }} spacing="xs" align="flex-start">
                                                        <Text color={colorPalette.errorTextCustomColor}>Remove SSO Connection</Text>
                                                        <Text color={colorPalette.errorTextCustomColor} size={'sm'}>
                                                            Removing this connection will disable current user accounts that use SSO and require users
                                                            to log in via email and password.
                                                        </Text>
                                                        <Button
                                                            color={colorPalette.errorTextCustomColor}
                                                            type="button"
                                                            onClick={() => {
                                                                setShowConfirmRemove(true);
                                                            }}
                                                        >
                                                            Remove SSO Connection
                                                        </Button>
                                                    </Stack>
                                                </Group>
                                            </Card>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>
                        <Divider color="gray.3" />
                        <PanelToolbar>
                            <Anchor target="_blank" href={configSvc.config.ssoDocumentationUrl}>
                                Documentation
                            </Anchor>
                            <Space sx={{ flex: 1 }} />
                            <Button type="button" disabled={isDisabled} onClick={handleSubmit}>
                                Submit Configuration
                            </Button>
                            <Button type="button" variant="outline" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </PanelToolbar>
                    </Stack>
                    <Divider orientation="vertical" color="gray.3" />
                    <Box p={20} style={{ backgroundColor: 'white', overflow: 'auto', minWidth: '500px' }}>
                        <Title order={4}>SAML Identity Provider Configuration</Title>
                        <Space h="lg" />

                        <a href={metadataUrl} download="cloudsaver-metadata.xml">
                            <Button type="button" rightIcon={<Download />} variant="outline" disabled={!configured}>
                                Download Metadata
                            </Button>
                        </a>

                        <Space h="lg" />
                        <Text size="sm">Signing Certificates</Text>
                        <Space h="sm" />

                        <List size="sm">
                            <List.Item>
                                <a href={cerUrl} download="cloudsaver-cer.xml">
                                    <Button px="sm" type="button" rightIcon={<Download />} variant="subtle" className={classes.hoverCerts}>
                                        CER Certificate
                                    </Button>
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href={pemUrl} download="cloudsaver-pem.xml">
                                    <Button px="sm" type="button" rightIcon={<Download />} variant="subtle" className={classes.hoverCerts}>
                                        PEM Certificate
                                    </Button>
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href={rawPemUrl} download="cloudsaver-pem.xml">
                                    <Button px="sm" type="button" rightIcon={<Download />} variant="subtle" className={classes.hoverCerts}>
                                        raw PEM Certificate
                                    </Button>
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href={pb7Url} download="cloudsaver-pem.xml">
                                    <Button px="sm" type="button" rightIcon={<Download />} variant="subtle" className={classes.hoverCerts}>
                                        PB7 Certificate
                                    </Button>
                                </a>
                            </List.Item>
                            <List.Item>
                                <a href={fingerprintUrl} download="cloudsaver-pem.xml">
                                    <Button px="sm" type="button" rightIcon={<Download />} variant="subtle" className={classes.hoverCerts}>
                                        Fingerprint Certificate
                                    </Button>
                                </a>
                            </List.Item>
                        </List>

                        <Space h="lg" />
                        <TextInput
                            label="Entity ID"
                            readOnly
                            value={entityId}
                            rightSection={
                                <CopyButton value={entityId}>
                                    {({ copied, copy }) => (
                                        <Button leftIcon={<Copy />} variant={copied ? 'filled' : 'default'} onClick={copy}>
                                            {copied ? 'Copied' : 'Copy'}
                                        </Button>
                                    )}
                                </CopyButton>
                            }
                            rightSectionWidth={100}
                        />

                        <Space h="lg" />
                        <TextInput
                            label="Single Sign-On URL"
                            readOnly
                            value={signOnUrl}
                            rightSection={
                                <CopyButton value={signOnUrl}>
                                    {({ copied, copy }) => (
                                        <Button leftIcon={<Copy />} variant={copied ? 'filled' : 'default'} onClick={copy}>
                                            {copied ? 'Copied' : 'Copy'}
                                        </Button>
                                    )}
                                </CopyButton>
                            }
                            rightSectionWidth={100}
                        />

                        <Space h="lg" />
                        <TextInput
                            label="Single Sign-Out URL"
                            readOnly
                            value={signOutUrl}
                            rightSection={
                                <CopyButton value={signOutUrl}>
                                    {({ copied, copy }) => (
                                        <Button leftIcon={<Copy />} variant={copied ? 'filled' : 'default'} onClick={copy}>
                                            {copied ? 'Copied' : 'Copy'}
                                        </Button>
                                    )}
                                </CopyButton>
                            }
                            rightSectionWidth={100}
                        />

                        <Space h="lg" />
                        <Text size="sm">Recommended Attributes</Text>
                        <Space h="xs" />
                        <List size="sm">
                            <List.Item>email</List.Item>
                            <List.Item>given_name</List.Item>
                            <List.Item>family_name</List.Item>
                        </List>
                    </Box>
                </Group>
            </PanelContent>
            <Modal opened={showConfirmRemove} title="Remove SSO Connection" onClose={() => setShowConfirmRemove(false)}>
                <Text size="sm" color="dimmed">
                    This SSO is currently configured. Deleting this connection will force users to log in via email and password. Are you sure you
                    want to continue?
                </Text>
                <Space h="lg" />
                <Text size="sm">Type "DELETE" to confirm</Text>
                <TextInput onChange={handleConfirmDeleteChange}></TextInput>
                <Space h="lg" />
                <Group position="right">
                    <Button type="button" variant="outline">
                        Back
                    </Button>
                    <Button type="button" color={colorPalette.errorTextCustomColor} disabled={disableConfirmRemove} onClick={handleRemoveSSO}>
                        Confirm Delete
                    </Button>
                </Group>
            </Modal>
        </PageContent>
    );
}

endpoint('configure-sso', ConfigureSSO, 'Configure Single Sign-On');

const useStyles = createStyles(() => ({
    hoverCerts: {
        color: theme.colors?.primary?.[6] as CustomColors,
        '&:hover': {
            color: theme.colors?.primary?.[7] as CustomColors,
            backgroundColor: colorPalette.white,
            textDecoration: 'underline',
        },
    },
}));
